import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
const errorMessageParser = ({ errors, t }) => {
   const isObject = errors !== null && typeof errors === 'object';
   const newObject = isObject ? errors : {};
   const keys = Object.keys(newObject);
   const memoizedErrors = keys.reduce((total, key) => {
      total[key] = t(errors?.[key]);
      return total;
   }, {});
   return memoizedErrors;
};
const errorParser = ({ result, data }) => {
   const errors = result?.error?.errors;
   const isObject = typeof data === 'object' && data;
   const keys = isObject ? Object.keys(data) : [];
   const errorsList = Array.isArray(errors) ? errors : [];
   const reduce = (total, key) => {
      const find = error => {
         const path = error?.path;
         const newPath = Array.isArray(path) ? path : [];
         const isError = newPath.includes(key);
         return isError;
      };
      const error = errorsList.find(find);
      const message = error?.message || '';
      total[key] = message;
      return total;
   };
   const newErrors = keys.reduce(reduce, {});
   return newErrors;
};
export const useValidate = schema => {
   const { t } = useTranslation('validation');
   const [errors, setErrors] = useState({});
   const memoizedErrors = useMemo(
      () => errorMessageParser({ errors, t }),
      [errors, t],
   );
   const clearErrors = () => setErrors({});
   const parseErrors = errors => errorMessageParser({ errors, t });
   const validate = ({ onSuccess = () => {}, onError = () => {}, data }) => {
      const result = schema.safeParse(data);
      if (result?.success) {
         onSuccess(result?.data);
      } else {
         const newErrors = errorParser({ result, data });
         setErrors(newErrors);
         onError(newErrors);
      }
   };
   return {
      clearErrors,
      errors,
      memoizedErrors,
      parseErrors,
      setErrors,
      validate,
   };
};
export const useArrayValidate = schema => {
   const { t } = useTranslation('validation');
   const [errors, setErrors] = useState([]);
   const memoizedErrors = useMemo(() => {
      const memoizedErrors = errors.map(errors =>
         errorMessageParser({ errors, t }),
      );
      return memoizedErrors;
   }, [errors, t]);
   const clearErrors = () => {
      const newErrors = errors.map(() => ({}));
      setErrors(newErrors);
   };
   const parseErrors = errors => {
      const newErrors = errors.map(errors => errorMessageParser({ errors, t }));
      return newErrors;
   };
   const validate = ({
      data = [],
      onError = () => {},
      onSuccess = () => {},
   }) => {
      const dataList = [];
      const errorsList = [];
      const isSuccess = data.every(data => {
         const result = schema.safeParse(data);
         const isSuccess = result?.success;
         const newData = result?.data;
         dataList.push(newData);
         const errors = errorParser({ result, data });
         const newErrors = isSuccess ? {} : errors;
         errorsList.push(newErrors);
         return isSuccess;
      });
      if (isSuccess) {
         onSuccess(dataList);
      } else {
         setErrors(errorsList);
         onError(errorsList);
      }
   };
   return {
      clearErrors,
      errors,
      memoizedErrors,
      parseErrors,
      setErrors,
      validate,
   };
};
